import * as React from "react";
import {
  applyConsent,
  clearTrackingCookies,
  ConsentChoices,
  CONSENT_VERSION,
  marketingVendors,
} from "./loadTrackers";

const STORAGE_KEY = "ids_cookie_consent";
const LEGACY_STORAGE_KEY = "cookie-consent-accepted";

interface StoredConsent extends ConsentChoices {
  version: string;
  timestamp: string;
}

interface ConsentContextValue {
  ready: boolean;
  consent: StoredConsent | null;
  settingsOpen: boolean;
  marketingAvailable: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  save: (choices: ConsentChoices) => void;
}

const noop = () => {};

const ConsentContext = React.createContext<ConsentContextValue>({
  ready: false,
  consent: null,
  settingsOpen: false,
  marketingAvailable: false,
  openSettings: noop,
  closeSettings: noop,
  save: noop,
});

function readStored(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    return parsed.version === CONSENT_VERSION ? parsed : null;
  } catch {
    return null;
  }
}

export const ConsentProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [ready, setReady] = React.useState(false);
  const [consent, setConsent] = React.useState<StoredConsent | null>(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  React.useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
    const stored = readStored();
    if (stored) applyConsent(stored);
    setConsent(stored);
    setReady(true);
  }, []);

  const save = React.useCallback(
    (choices: ConsentChoices) => {
      const next: StoredConsent = {
        statistics: choices.statistics,
        marketing: choices.marketing && marketingVendors.length > 0,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
      const revoked =
        (consent?.statistics && !next.statistics) || (consent?.marketing && !next.marketing);
      setConsent(next);
      setSettingsOpen(false);
      if (revoked) {
        clearTrackingCookies();
        // Already-loaded third-party scripts can only be unloaded by a reload.
        window.location.reload();
        return;
      }
      applyConsent(next);
    },
    [consent]
  );

  const value = React.useMemo<ConsentContextValue>(
    () => ({
      ready,
      consent,
      settingsOpen,
      marketingAvailable: marketingVendors.length > 0,
      openSettings: () => setSettingsOpen(true),
      closeSettings: () => setSettingsOpen(false),
      save,
    }),
    [ready, consent, settingsOpen, save]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => React.useContext(ConsentContext);
